// src/pages/QuestionnairePage.jsx
/* ------------------------------------------------------------------ */
/* QuestionnairePage – dynamic assessment hub                         */
/* ------------------------------------------------------------------ */
/**
 * Routes handled
 * --------------
 * • `/questionnaire`                 → `<QuestionnaireChooser />`
 * • `/questionnaire/:questionnaire`  → `<DynamicQuestionnaireForm />`
 *
 * Design notes
 * ------------
 * 1. The “chooser” UI was split into its own component for clarity and reuse.
 * 2. The list of available assessments lives in a single **`QUESTIONNAIRES`**
 *    array so adding / removing a questionnaire is a single-line diff and
 *    easily unit-testable.
 *
 * Future work
 * -----------
 * • Move `QUESTIONNAIRES` to `utils/questionnaireRegistry.js` if other
 *   surfaces (e.g. dashboard, side-menu) also need the list.
 * • Promote `<QuestionnaireChooser/>` into `components/questionnaire/`
 *   if a mobile layout or other page needs to re-use it.
 *
 * @component
 * @returns {JSX.Element}
 */

import React, { memo, useEffect, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import DynamicQuestionnaireForm from "../components/questionnaire/DynamicQuestionnaireForm";
import { QuestionnaireChooser } from "../components/questionnaire/QuestionnaireChooser";
import { trackEvent } from "../utils/analytics/track";
import Spinner from "../components/Spinner";

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */
function QuestionnairePage({
  permittedQuestionnaires = [],
  isQuestionnairePermitted = () => false,
  loadingQuestionnaires = false,
}) {
  const { questionnaireName } = useParams();
  const lastTrackedRef = useRef(null);

  /* ---------- No slug → show list ----------------------------- */
  useEffect(() => {
    if (loadingQuestionnaires) return;
    if (!permittedQuestionnaires.length) return;
    if (questionnaireName && !isQuestionnairePermitted(questionnaireName)) {
      return;
    }

    // Track chooser view when no questionnaire is selected
    if (!questionnaireName) {
      if (lastTrackedRef.current !== "chooser") {
        trackEvent("questionnaires_view");
        lastTrackedRef.current = "chooser";
      }
      return;
    }

    // Track questionnaire open when a questionnaire is selected
    const key = `q:${questionnaireName}`;
    if (lastTrackedRef.current !== key) {
      trackEvent("questionnaire_open", { questionnaire: questionnaireName });
      lastTrackedRef.current = key;
    }
  }, [
    questionnaireName,
    loadingQuestionnaires,
    permittedQuestionnaires,
    isQuestionnairePermitted,
  ]);

  if (loadingQuestionnaires) {
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!permittedQuestionnaires.length) {
    return <Navigate to="/home" replace />;
  }

  if (questionnaireName && !isQuestionnairePermitted(questionnaireName)) {
    return <Navigate to="/questionnaires" replace />;
  }

  if (!questionnaireName) {
    return (
      <QuestionnaireChooser
        permittedQuestionnaires={permittedQuestionnaires}
      />
    );
  }

  return (
    <div className="form-page w-full h-full max-w-lg p-4">
      <DynamicQuestionnaireForm />
    </div>
  );
}

export default memo(QuestionnairePage);
